import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Brain,
    Car,
    Loader2,
    RefreshCw,
    Scan,
    Scale,
    Sparkles,
    Terminal,
    Upload,
    AlertTriangle,
    Eye,
    CheckCircle,
    XCircle,
    ShieldAlert,
    Activity,
    Bot,
    User,
    ChevronDown,
    ChevronUp,
    Mail,
    TrendingUp,
    Compass,
    Repeat,
    Check,
    X,
    MessageSquare,
    FileText,
    Settings,
    Play,
    Globe,
} from "lucide-react";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import AkilWidgetControlPanel from "../../components/ai/AkilWidgetControlPanel";
import AkilSiteWidgetPanel from "../../components/ai/AkilSiteWidgetPanel";
import { AiMarkdown } from "../../components/ai/AiMarkdown";
import {
    akilMerkezDashboard,
    akilMerkezKomut,
    akilOcrDegerKaybi,
    akilOgrenmeSync,
    akilTahkimDkPaket,
    akilMerkezOtonomRun,
    akilAjanGorevleri,
    akilAjanGoreviIslem,
    akilDosyaSaglikUyarilari,
    akilRunHealthCheck,
    akilUltraServices,
    akilUltraServicesAction,
} from "../../lib/akilApi";
import { AkilHafizaTab, AkilTahminTab } from "../../components/ai/AkilHafizaTahminPanel";
import { setSonHesap } from "../../lib/akilContext";

function Kpi({ label, value, sub, tone }) {
    const borderTone =
        tone === "red"
            ? "border-rose-500/30 bg-rose-500/5 text-rose-200"
            : tone === "green"
              ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
              : "border-[#D4AF37]/20 bg-[#121A2F]/80 text-white";

    return (
        <div className={`rounded-xl border p-4 ${borderTone}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl mt-1">{value ?? "—"}</div>
            {sub ? <div className="text-[11px] text-slate-500 mt-1">{sub}</div> : null}
        </div>
    );
}

export default function AkilMerkezPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("ozet");
    const [komut, setKomut] = useState("");
    const [komutSonuc, setKomutSonuc] = useState(null);
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrBelgeTipi, setOcrBelgeTipi] = useState("hasar_faturasi");
    const [ocrSonuc, setOcrSonuc] = useState(null);
    const [paketDosya, setPaketDosya] = useState("");
    const [paketSonuc, setPaketSonuc] = useState(null);

    // Advanced features states
    const [ajanGorevleri, setAjanGorevleri] = useState([]);
    const [saglikUyarilari, setSaglikUyarilari] = useState([]);
    const [healthChecking, setHealthChecking] = useState(false);
    const [expandedGorev, setExpandedGorev] = useState(null);
    const [otonomRunning, setOtonomRunning] = useState(false);

    // Ultra features states
    const [ultraData, setUltraData] = useState(null);
    const [ultraActionLoading, setUltraActionLoading] = useState(false);
    const [ultraSubTab, setUltraSubTab] = useState("mail");
    const [expandedUltraItem, setExpandedUltraItem] = useState(null);
    const [simMailId, setSimMailId] = useState("");
    const [simDosyaId, setSimDosyaId] = useState("");
    const [simGonderimId, setSimGonderimId] = useState("");
    const [simBasarili, setSimBasarili] = useState(true);
    const [simSüre, setSimSüre] = useState("2");

    const yukle = useCallback(async () => {
        setLoading(true);
        try {
            const d = await akilMerkezDashboard(30);
            setData(d);
            
            // Ajan görevlerini, sağlık uyarılarını ve ultra verileri yükle
            const [gRes, uRes, ultraRes] = await Promise.all([
                akilAjanGorevleri(),
                akilDosyaSaglikUyarilari(),
                akilUltraServices()
            ]);
            setAjanGorevleri(gRes.results || []);
            setSaglikUyarilari(uRes.results || []);
            setUltraData(ultraRes || null);
        } catch (e) {
            toast.error(e.message || "Merkez verileri yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, []);

    const handleUltraAction = async (payload) => {
        setUltraActionLoading(true);
        try {
            const r = await akilUltraServicesAction(payload);
            if (r.success) {
                toast.success("İşlem başarıyla tamamlandı");
                const uRes = await akilUltraServices();
                setUltraData(uRes);
                await yukle();
            } else {
                toast.error(r.error || "İşlem başarısız");
            }
        } catch (e) {
            toast.error(e.response?.data?.error || e.message || "İşlem hatası");
        } finally {
            setUltraActionLoading(false);
        }
    };

    useEffect(() => {
        yukle();
    }, [yukle]);

    const runKomut = async () => {
        if (!komut.trim()) return;
        try {
            const r = await akilMerkezKomut({ komut: komut.trim() });
            setKomutSonuc(r);
            if (r.gercek_deger_kaybi) setSonHesap(r);
            toast.success("Komut tamamlandı");
        } catch (e) {
            toast.error(e.response?.data?.error || e.message);
        }
    };

    const ocrUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setOcrLoading(true);
        setOcrSonuc(null);
        try {
            const r = await akilOcrDegerKaybi(f, ocrBelgeTipi);
            setOcrSonuc(r);
            if (r.success && r.hesap) {
                setSonHesap(r.hesap);
                setKomutSonuc(r.hesap);
                const tahmini = r.rayic_tahmini ? " (rayiç tahmini)" : "";
                toast.success(
                    `DK: ₺${Math.round(r.hesap.gercek_deger_kaybi).toLocaleString("tr-TR")}${tahmini}`
                );
            } else {
                toast.error(r.error || "OCR hesap tamamlanamadı", {
                    description: r.hint || (r.eksik_alanlar || []).join(", ") || undefined,
                    duration: 8000,
                });
                setKomutSonuc(r);
            }
        } catch (err) {
            const d = err.response?.data;
            toast.error(d?.error || err.message || "OCR isteği başarısız");
            if (d) {
                setOcrSonuc(d);
                setKomutSonuc(d);
            }
        } finally {
            setOcrLoading(false);
            e.target.value = "";
        }
    };

    const tahkimPaket = async () => {
        if (!paketDosya.trim()) return toast.error("Dosya no girin");
        try {
            const r = await akilTahkimDkPaket({ dosya_no: paketDosya.trim() });
            setPaketSonuc(r);
            if (r.deger_kaybi_hesap?.success) setSonHesap(r.deger_kaybi_hesap);
        } catch (e) {
            toast.error(e.response?.data?.error || e.message);
        }
    };

    const ogrenmeSync = async () => {
        try {
            const r = await akilOgrenmeSync(100);
            toast.success(`Öğrenme sync: ${r.toplam_bilgi} bilgi parçası`);
            yukle();
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handleAjanIslem = async (gorevId, action) => {
        try {
            const r = await akilAjanGoreviIslem({ gorev_id: gorevId, action });
            if (r.success) {
                toast.success(action === "approve" ? "Görev onaylandı ve uygulandı" : "Görev reddedildi");
                // Listeyi güncelle
                const gRes = await akilAjanGorevleri();
                setAjanGorevleri(gRes.results || []);
            }
        } catch (e) {
            toast.error(e.message);
        }
    };

    const startHealthCheck = async () => {
        setHealthChecking(true);
        try {
            const r = await akilRunHealthCheck();
            if (r.success) {
                toast.success(`Tarama tamamlandı! ${r.olusturman_uyari || r.olusturulan_uyari || 0} yeni uyarı bulundu.`);
                const uRes = await akilDosyaSaglikUyarilari();
                setSaglikUyarilari(uRes.results || []);
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setHealthChecking(false);
        }
    };

    const runOtonom = async () => {
        setOtonomRunning(true);
        try {
            const r = await akilMerkezOtonomRun();
            if (r?.success) {
                const queued = r?.otonom_evrak?.queued || 0;
                const applied = r?.rule_engine?.uygulanan || 0;
                toast.success(`Otonom ajan tamamlandi: ${queued} kuyruk, ${applied} kural aksiyonu`);
            } else {
                toast.error(r?.error || "Otonom ajan calistirilamadi");
            }
            await yukle();
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Otonom ajan hatasi");
        } finally {
            setOtonomRunning(false);
        }
    };

    const tabs = [
        { id: "ozet", label: "Özet" },
        { id: "hafiza", label: "Kurumsal Hafıza" },
        { id: "tahmin", label: "Sonuç Tahmini" },
        { id: "site-widget", label: "Site Widget" },
        { id: "ultra", label: "Ultra Akıl Kontrol" },
        { id: "ajanlar", label: `Ajanlar (${ajanGorevleri.filter(g => g.durum === "ONAY_BEKLIYOR").length})` },
        { id: "saglik", label: `Dosya Sağlığı (${saglikUyarilari.length})` },
        { id: "ocr", label: "OCR → DK" },
        { id: "komut", label: "Komut Paleti" },
        { id: "tahkim", label: "Tahkim+DK" },
        { id: "kritik", label: "Kritik Dosyalar" },
    ];

    return (
        <AiPageShell
            title="Akıl Merkez"
            subtitle="Tüm yapay zeka modüllerini tek yerden yönetin — Öğrenen Akıl, Çoklu Ajanlar, Dosya Sağlığı, OCR."
            actions={
                <button
                    type="button"
                    onClick={yukle}
                    className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-white/10 hover:border-[#D4AF37]/40"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
                </button>
            }
        >
            {loading && !data ? (
                <div className="flex justify-center py-20 text-slate-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : (
                <>
                    <AkilWidgetControlPanel />

                    <div className="flex flex-wrap gap-2 mb-6 mt-4">
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                className={`px-4 py-2 rounded-full text-sm border transition-all ${
                                    tab === t.id
                                        ? "bg-[#D4AF37]/20 border-[#D4AF37]/50 text-[#D4AF37]"
                                        : "border-white/10 text-slate-400 hover:border-white/20"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "ozet" && data && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <Kpi label="Bilgi bankası" value={data.bilgi_bankasi_toplam} />
                                <Kpi
                                    label="DK geçmişi"
                                    value={data.deger_kaybi_ogrenme?.toplam_hesap}
                                />
                                <Kpi label="Sağlık Uyarısı" value={saglikUyarilari.length} tone={saglikUyarilari.length > 0 ? "red" : "green"} />
                                <Kpi label="Ajan Onay Bekleyen" value={ajanGorevleri.filter(g => g.durum === "ONAY_BEKLIYOR").length} tone={ajanGorevleri.filter(g => g.durum === "ONAY_BEKLIYOR").length > 0 ? "red" : "green"} />
                            </div>
                            <AiMarkdown text={data.metin || ""} />
                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                                {(data.moduller || []).map((m) => (
                                    <Link
                                        key={m.id}
                                        to={m.path}
                                        className="rounded-xl border border-white/10 p-4 hover:border-[#D4AF37]/40 transition flex items-center gap-3"
                                    >
                                        <Sparkles className="h-5 w-5 text-[#D4AF37]" />
                                        <span className="text-sm text-slate-200">{m.label}</span>
                                    </Link>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={ogrenmeSync}
                                className="text-sm px-4 py-2 rounded-lg bg-[#D4AF37] text-black font-medium hover:bg-[#bfa02e] transition"
                            >
                                Öğrenme senkronizasyonu çalıştır
                            </button>
                            <button
                                type="button"
                                onClick={runOtonom}
                                disabled={otonomRunning}
                                className="ml-3 text-sm px-4 py-2 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-500 disabled:opacity-50 transition"
                            >
                                {otonomRunning ? "Otonom ajan calisiyor..." : "Ultra Otonom Ajanı Calistir"}
                            </button>

                            <div className="rounded-xl border border-violet-400/30 bg-violet-500/5 p-4">
                                <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-2">
                                    Ultra Otonom Kesif
                                </div>
                                <div className="grid md:grid-cols-3 gap-3">
                                    <Kpi
                                        label="Event stream"
                                        value={data?.ultra_otomasyon?.event_stream?.event_count || 0}
                                        sub="Son 24 saat yakalanan olay"
                                    />
                                    <Kpi
                                        label="Queue pending"
                                        value={data?.ultra_otomasyon?.queue_orchestrator?.pending_total || 0}
                                        sub="Onceliklendirilmis gonderim kuyrugu"
                                    />
                                    <Kpi
                                        label="Self-healing hata"
                                        value={data?.ultra_otomasyon?.self_healing?.error_count || 0}
                                        sub="Fallback bekleyen hata kaydi"
                                        tone={(data?.ultra_otomasyon?.self_healing?.error_count || 0) > 0 ? "red" : "green"}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === "hafiza" && <AkilHafizaTab />}

                    {tab === "tahmin" && <AkilTahminTab />}

                    {tab === "site-widget" && <AkilSiteWidgetPanel />}

                    {tab === "ultra" && (
                        <div className="space-y-6">
                            <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#121A2F]/60 p-6">
                                <div className="flex items-center gap-3 text-[#D4AF37] mb-2">
                                    <Brain className="h-6 w-6 animate-pulse" />
                                    <h3 className="font-display text-xl font-bold">Ultra Gelişmiş Akıl Yönetim Paneli</h3>
                                </div>
                                <p className="text-sm text-slate-300 leading-relaxed">
                                    Akıl'ın en üst düzey otonom yeteneklerini, yapay zeka karar destek mekanizmalarını, 
                                    sanal katip iş akışlarını ve pekiştirmeli öğrenme modellerini buradan canlı olarak izleyin, 
                                    simüle edin ve denetleyin.
                                </p>
                            </div>

                            {/* Ultra Sub Tabs */}
                            <div className="flex border-b border-white/10 gap-2 overflow-x-auto pb-px">
                                <button
                                    type="button"
                                    onClick={() => { setUltraSubTab("mail"); setExpandedUltraItem(null); }}
                                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all shrink-0 flex items-center gap-2 ${
                                        ultraSubTab === "mail"
                                            ? "border-[#D4AF37] text-[#D4AF37]"
                                            : "border-transparent text-slate-400 hover:text-slate-200"
                                    }`}
                                >
                                    <Mail className="h-4 w-4" /> Otonom E-Posta / KEP
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setUltraSubTab("negotiation"); setExpandedUltraItem(null); }}
                                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all shrink-0 flex items-center gap-2 ${
                                        ultraSubTab === "negotiation"
                                            ? "border-[#D4AF37] text-[#D4AF37]"
                                            : "border-transparent text-slate-400 hover:text-slate-200"
                                    }`}
                                >
                                    <TrendingUp className="h-4 w-4" /> Akıllı Pazarlık & Uzlaşma
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setUltraSubTab("katip"); setExpandedUltraItem(null); }}
                                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all shrink-0 flex items-center gap-2 ${
                                        ultraSubTab === "katip"
                                            ? "border-[#D4AF37] text-[#D4AF37]"
                                            : "border-transparent text-slate-400 hover:text-slate-200"
                                    }`}
                                >
                                    <Compass className="h-4 w-4" /> Sanal Katip Ajanı (UYAP)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setUltraSubTab("learning"); setExpandedUltraItem(null); }}
                                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all shrink-0 flex items-center gap-2 ${
                                        ultraSubTab === "learning"
                                            ? "border-[#D4AF37] text-[#D4AF37]"
                                            : "border-transparent text-slate-400 hover:text-slate-200"
                                    }`}
                                >
                                    <Repeat className="h-4 w-4" /> Kapalı Döngü Öğrenme
                                </button>
                            </div>

                            {/* Sub Tab Content: Mail */}
                            {ultraSubTab === "mail" && (
                                <div className="space-y-6">
                                    <div className="grid lg:grid-cols-3 gap-6">
                                        {/* Simulation Form */}
                                        <div className="lg:col-span-1 rounded-xl border border-white/10 bg-[#0B0F19]/60 p-5 space-y-4 h-fit">
                                            <div className="flex items-center gap-2 text-[#D4AF37]">
                                                <Settings className="h-4 w-4" />
                                                <h4 className="text-sm font-bold">E-Posta Analiz Simülatörü</h4>
                                            </div>
                                            <p className="text-xs text-slate-400">
                                                Sisteme gelen bir e-posta veya KEP iletisinin Akıl tarafından otonom analiz edilmesini, 
                                                ilgili dosyanın bulunmasını ve aksiyon önerisi üretilmesini simüle edin.
                                            </p>
                                            <div className="space-y-2">
                                                <label className="text-xs text-slate-400">Gelen E-Posta ID</label>
                                                <input
                                                    type="number"
                                                    value={simMailId}
                                                    onChange={(e) => setSimMailId(e.target.value)}
                                                    placeholder="Örn: 1"
                                                    className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleUltraAction({ action: "process_mail", eposta_id: simMailId })}
                                                disabled={ultraActionLoading || !simMailId}
                                                className="w-full flex items-center justify-center gap-2 text-xs px-4 py-2.5 rounded-lg bg-[#D4AF37] text-black font-semibold hover:bg-[#bfa02e] disabled:opacity-50 transition"
                                            >
                                                {ultraActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                                                Otonom Analizi Başlat
                                            </button>
                                        </div>

                                        {/* List */}
                                        <div className="lg:col-span-2 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-bold text-white">Son E-Posta / KEP Analizleri ve Aksiyonları</h4>
                                                <span className="text-xs text-slate-500">Toplam: {ultraData?.otonom_aksiyonlar?.length || 0}</span>
                                            </div>

                                            {(!ultraData?.otonom_aksiyonlar || ultraData.otonom_aksiyonlar.length === 0) ? (
                                                <div className="text-slate-500 text-sm py-10 text-center border border-white/5 bg-black/20 rounded-xl">
                                                    Henüz otonom e-posta analiz kaydı bulunmuyor.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {ultraData.otonom_aksiyonlar.map((item) => {
                                                        const isExpanded = expandedUltraItem === item.id;
                                                        return (
                                                            <div key={item.id} className="rounded-xl border border-white/10 bg-[#0B0F19]/40 overflow-hidden">
                                                                <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
                                                                    <div className="space-y-1">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">
                                                                                {item.aksiyon_tipi}
                                                                            </span>
                                                                            {item.dosya_no && (
                                                                                <span className="text-xs font-mono text-slate-400">
                                                                                    Dosya: {item.dosya_no} ({item.plaka})
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <h5 className="text-sm font-semibold text-white">{item.baslik}</h5>
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                                                            item.durum === "ONAYLANDI"
                                                                                ? "bg-emerald-500/10 text-emerald-400"
                                                                                : item.durum === "REDDEDILDI"
                                                                                  ? "bg-rose-500/10 text-rose-400"
                                                                                  : "bg-amber-500/10 text-amber-400"
                                                                        }`}>
                                                                            {item.durum === "ONAY_BEKLIYOR" ? "Onay Bekliyor" : item.durum}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedUltraItem(isExpanded ? null : item.id)}
                                                                            className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400"
                                                                        >
                                                                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {isExpanded && (
                                                                    <div className="border-t border-white/10 p-4 space-y-4 bg-black/30">
                                                                        <div className="space-y-1">
                                                                            <div className="text-xs font-bold text-slate-400">TESPİT ÖZETİ</div>
                                                                            <p className="text-xs text-slate-300 bg-black/40 p-3 rounded-lg border border-white/5 leading-relaxed">
                                                                                {item.detay || "Detay belirtilmemiş."}
                                                                            </p>
                                                                        </div>

                                                                        {item.onerilen_aksiyon && (
                                                                            <div className="grid md:grid-cols-2 gap-4">
                                                                                {item.onerilen_aksiyon.dilekce_taslagi && (
                                                                                    <div className="space-y-1.5">
                                                                                        <div className="flex items-center justify-between">
                                                                                            <span className="text-xs font-bold text-[#D4AF37]">TAHKİM BAŞVURU DİLEKÇESİ TASLAĞI</span>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    navigator.clipboard.writeText(item.onerilen_aksiyon.dilekce_taslagi);
                                                                                                    toast.success("Dilekçe taslağı kopyalandı");
                                                                                                }}
                                                                                                className="text-[10px] text-slate-400 hover:text-white underline"
                                                                                            >
                                                                                                Kopyala
                                                                                            </button>
                                                                                        </div>
                                                                                        <textarea
                                                                                            readOnly
                                                                                            value={item.onerilen_aksiyon.dilekce_taslagi}
                                                                                            className="w-full h-40 rounded-lg bg-black/50 border border-white/5 p-2.5 text-xs font-mono text-slate-300 focus:outline-none"
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                                {item.onerilen_aksiyon.whatsapp_mesaji && (
                                                                                    <div className="space-y-1.5">
                                                                                        <div className="flex items-center justify-between">
                                                                                            <span className="text-xs font-bold text-emerald-400">MÜVEKKİL WHATSAPP BİLGİLENDİRME MESAJI</span>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    navigator.clipboard.writeText(item.onerilen_aksiyon.whatsapp_mesaji);
                                                                                                    toast.success("WhatsApp mesajı kopyalandı");
                                                                                                }}
                                                                                                className="text-[10px] text-slate-400 hover:text-white underline"
                                                                                            >
                                                                                                Kopyala
                                                                                            </button>
                                                                                        </div>
                                                                                        <textarea
                                                                                            readOnly
                                                                                            value={item.onerilen_aksiyon.whatsapp_mesaji}
                                                                                            className="w-full h-40 rounded-lg bg-black/50 border border-white/5 p-2.5 text-xs font-mono text-slate-300 focus:outline-none"
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {item.durum === "ONAY_BEKLIYOR" && (
                                                                            <div className="flex justify-end gap-2 pt-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleUltraAction({ action: "update_status", log_type: "otonom", log_id: item.id, status: "REDDEDILDI" })}
                                                                                    disabled={ultraActionLoading}
                                                                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 transition"
                                                                                >
                                                                                    <X className="h-3.5 w-3.5" /> Reddet
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleUltraAction({ action: "update_status", log_type: "otonom", log_id: item.id, status: "ONAYLANDI" })}
                                                                                    disabled={ultraActionLoading}
                                                                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition"
                                                                                >
                                                                                    <Check className="h-3.5 w-3.5" /> Onayla ve Uygula
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sub Tab Content: Negotiation */}
                            {ultraSubTab === "negotiation" && (
                                <div className="space-y-6">
                                    <div className="grid lg:grid-cols-3 gap-6">
                                        {/* Simulation Form */}
                                        <div className="lg:col-span-1 rounded-xl border border-white/10 bg-[#0B0F19]/60 p-5 space-y-4 h-fit">
                                            <div className="flex items-center gap-2 text-[#D4AF37]">
                                                <Settings className="h-4 w-4" />
                                                <h4 className="text-sm font-bold">Pazarlık Stratejisi Üretici</h4>
                                            </div>
                                            <p className="text-xs text-slate-400">
                                                İlgili sigorta dosyasının detaylarını ve sigorta şirketinin geçmiş ödeme davranışlarını 
                                                analiz ederek akıllı pazarlık stratejisi ve mutabakat teklif metni üretin.
                                            </p>
                                            <div className="space-y-2">
                                                <label className="text-xs text-slate-400">Sigorta Dosyası ID</label>
                                                <input
                                                    type="number"
                                                    value={simDosyaId}
                                                    onChange={(e) => setSimDosyaId(e.target.value)}
                                                    placeholder="Örn: 12"
                                                    className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleUltraAction({ action: "negotiation_strategy", dosya_id: simDosyaId })}
                                                disabled={ultraActionLoading || !simDosyaId}
                                                className="w-full flex items-center justify-center gap-2 text-xs px-4 py-2.5 rounded-lg bg-[#D4AF37] text-black font-semibold hover:bg-[#bfa02e] disabled:opacity-50 transition"
                                            >
                                                {ultraActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                                                Strateji & Teklif Üret
                                            </button>
                                        </div>

                                        {/* List */}
                                        <div className="lg:col-span-2 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-bold text-white">Akıllı Pazarlık ve Mutabakat Önerileri</h4>
                                                <span className="text-xs text-slate-500">Toplam: {ultraData?.pazarliklar?.length || 0}</span>
                                            </div>

                                            {(!ultraData?.pazarliklar || ultraData.pazarliklar.length === 0) ? (
                                                <div className="text-slate-500 text-sm py-10 text-center border border-white/5 bg-black/20 rounded-xl">
                                                    Henüz pazarlık stratejisi kaydı bulunmuyor.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {ultraData.pazarliklar.map((item) => {
                                                        const isExpanded = expandedUltraItem === item.id;
                                                        return (
                                                            <div key={item.id} className="rounded-xl border border-white/10 bg-[#0B0F19]/40 overflow-hidden">
                                                                <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
                                                                    <div className="space-y-1">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">
                                                                                {item.sigorta_sirketi}
                                                                            </span>
                                                                            <span className="text-xs font-mono text-slate-400">
                                                                                Dosya: {item.dosya_no} ({item.plaka})
                                                                            </span>
                                                                        </div>
                                                                        <h5 className="text-sm font-semibold text-white">
                                                                            Tavsiye Edilen Mutabakat Teklifi: <span className="text-[#D4AF37]">₺{item.tavsiye_edilen_teklif.toLocaleString("tr-TR")}</span>
                                                                        </h5>
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                                                            item.durum === "KABUL"
                                                                                ? "bg-emerald-500/10 text-emerald-400"
                                                                                : item.durum === "RED"
                                                                                  ? "bg-rose-500/10 text-rose-400"
                                                                                  : "bg-amber-500/10 text-amber-400"
                                                                        }`}>
                                                                            {item.durum === "TASLAK" ? "Taslak" : item.durum}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedUltraItem(isExpanded ? null : item.id)}
                                                                            className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400"
                                                                        >
                                                                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {isExpanded && (
                                                                    <div className="border-t border-white/10 p-4 space-y-4 bg-black/30">
                                                                        <div className="space-y-1">
                                                                            <div className="text-xs font-bold text-slate-400">ANALİZ VE PAZARLIK STRATEJİSİ</div>
                                                                            <div className="text-xs text-slate-300 bg-black/40 p-3 rounded-lg border border-white/5 leading-relaxed whitespace-pre-wrap">
                                                                                {item.analiz_ve_strateji}
                                                                            </div>
                                                                        </div>

                                                                        <div className="space-y-1.5">
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="text-xs font-bold text-emerald-400">SİGORTA ŞİRKETİ MUTABAKAT TEKLİF YAZISI</span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        navigator.clipboard.writeText(item.teklif_metni);
                                                                                        toast.success("Teklif metni kopyalandı");
                                                                                    }}
                                                                                    className="text-[10px] text-slate-400 hover:text-white underline"
                                                                                >
                                                                                    Kopyala
                                                                                </button>
                                                                            </div>
                                                                            <textarea
                                                                                readOnly
                                                                                value={item.teklif_metni}
                                                                                className="w-full h-40 rounded-lg bg-black/50 border border-white/5 p-2.5 text-xs font-mono text-slate-300 focus:outline-none"
                                                                            />
                                                                        </div>

                                                                        {item.durum === "TASLAK" && (
                                                                            <div className="flex justify-end gap-2 pt-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleUltraAction({ action: "update_status", log_type: "pazarlik", log_id: item.id, status: "RED" })}
                                                                                    disabled={ultraActionLoading}
                                                                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 transition"
                                                                                >
                                                                                    <X className="h-3.5 w-3.5" /> Reddet
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleUltraAction({ action: "update_status", log_type: "pazarlik", log_id: item.id, status: "KABUL" })}
                                                                                    disabled={ultraActionLoading}
                                                                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition"
                                                                                >
                                                                                    <Check className="h-3.5 w-3.5" /> Kabul Edildi Olarak İşaretle
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sub Tab Content: Katip */}
                            {ultraSubTab === "katip" && (
                                <div className="space-y-6">
                                    <div className="grid lg:grid-cols-3 gap-6">
                                        {/* Simulation Form */}
                                        <div className="lg:col-span-1 rounded-xl border border-white/10 bg-[#0B0F19]/60 p-5 space-y-4 h-fit">
                                            <div className="flex items-center gap-2 text-[#D4AF37]">
                                                <Settings className="h-4 w-4" />
                                                <h4 className="text-sm font-bold">Sanal Katip Sorgulama Simülatörü</h4>
                                            </div>
                                            <p className="text-xs text-slate-400">
                                                Tahkim Komisyonu portalı veya UYAP sorgulamasını simüle edin. Hakem atamaları, 
                                                bilirkişi raporu yüklenmesi gibi olayları tespit edip itiraz dilekçesi taslağı hazırlatın.
                                            </p>
                                            <div className="space-y-2">
                                                <label className="text-xs text-slate-400">Sigorta Dosyası ID</label>
                                                <input
                                                    type="number"
                                                    value={simDosyaId}
                                                    onChange={(e) => setSimDosyaId(e.target.value)}
                                                    placeholder="Örn: 5"
                                                    className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleUltraAction({ action: "sanal_katip", dosya_id: simDosyaId })}
                                                disabled={ultraActionLoading || !simDosyaId}
                                                className="w-full flex items-center justify-center gap-2 text-xs px-4 py-2.5 rounded-lg bg-[#D4AF37] text-black font-semibold hover:bg-[#bfa02e] disabled:opacity-50 transition"
                                            >
                                                {ultraActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                                                Sorgulamayı Tetikle
                                            </button>
                                        </div>

                                        {/* List */}
                                        <div className="lg:col-span-2 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-bold text-white">Sanal Katip Portal Sorgulamaları ve Bulguları</h4>
                                                <span className="text-xs text-slate-500">Toplam: {ultraData?.sanal_katip?.length || 0}</span>
                                            </div>

                                            {(!ultraData?.sanal_katip || ultraData.sanal_katip.length === 0) ? (
                                                <div className="text-slate-500 text-sm py-10 text-center border border-white/5 bg-black/20 rounded-xl">
                                                    Henüz sanal katip sorgulama kaydı bulunmuyor.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {ultraData.sanal_katip.map((item) => {
                                                        const isExpanded = expandedUltraItem === item.id;
                                                        return (
                                                            <div key={item.id} className="rounded-xl border border-white/10 bg-[#0B0F19]/40 overflow-hidden">
                                                                <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
                                                                    <div className="space-y-1">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">
                                                                                {item.gorev_tipi}
                                                                            </span>
                                                                            <span className="text-xs font-mono text-slate-400">
                                                                                Dosya: {item.dosya_no} ({item.plaka})
                                                                            </span>
                                                                        </div>
                                                                        <h5 className="text-sm font-semibold text-white">
                                                                            Bulgu: <span className="text-blue-300">{item.bulgular?.olay || "Sorgulama Tamamlandı"}</span>
                                                                        </h5>
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                                                            item.durum === "TAMAMLANDI"
                                                                                ? "bg-emerald-500/10 text-emerald-400"
                                                                                : item.durum === "HATA"
                                                                                  ? "bg-rose-500/10 text-rose-400"
                                                                                  : "bg-amber-500/10 text-amber-400"
                                                                        }`}>
                                                                            {item.durum}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedUltraItem(isExpanded ? null : item.id)}
                                                                            className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400"
                                                                        >
                                                                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {isExpanded && (
                                                                    <div className="border-t border-white/10 p-4 space-y-4 bg-black/30">
                                                                        <div className="space-y-1">
                                                                            <div className="text-xs font-bold text-slate-400">SORGULAMA BULGULARI</div>
                                                                            <div className="text-xs text-slate-300 bg-black/40 p-3 rounded-lg border border-white/5 leading-relaxed">
                                                                                <div><strong>Olay:</strong> {item.bulgular?.olay}</div>
                                                                                {item.bulgular?.hakem_adi && <div><strong>Hakem:</strong> {item.bulgular.hakem_adi}</div>}
                                                                                {item.bulgular?.durusma_tarihi && <div><strong>Duruşma Tarihi:</strong> {item.bulgular.durusma_tarihi}</div>}
                                                                                {item.bulgular?.talep_edilen_deger_kaybi && (
                                                                                    <div className="mt-1">
                                                                                        <strong>Talep Edilen:</strong> ₺{item.bulgular.talep_edilen_deger_kaybi.toLocaleString("tr-TR")} · 
                                                                                        <strong> Bilirkişi Hesaplanan:</strong> ₺{item.bulgular.bilirkisi_hesaplanan_deger_kaybi.toLocaleString("tr-TR")} · 
                                                                                        <strong className="text-rose-400"> Fark:</strong> ₺{item.bulgular.fark.toLocaleString("tr-TR")} (%{item.bulgular.fark_yuzde} eksik)
                                                                                    </div>
                                                                                )}
                                                                                <div className="mt-2 pt-2 border-t border-white/5 text-slate-300 italic">
                                                                                    {item.bulgular?.analiz}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {item.itiraz_dilekce_taslagi && (
                                                                            <div className="space-y-1.5">
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="text-xs font-bold text-[#D4AF37]">BİLİRKİŞİ RAPORUNA İTİRAZ DİLEKÇESİ TASLAĞI</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            navigator.clipboard.writeText(item.itiraz_dilekce_taslagi);
                                                                                            toast.success("İtiraz dilekçesi kopyalandı");
                                                                                        }}
                                                                                        className="text-[10px] text-slate-400 hover:text-white underline"
                                                                                    >
                                                                                        Kopyala
                                                                                    </button>
                                                                                </div>
                                                                                <textarea
                                                                                    readOnly
                                                                                    value={item.itiraz_dilekce_taslagi}
                                                                                    className="w-full h-40 rounded-lg bg-black/50 border border-white/5 p-2.5 text-xs font-mono text-slate-300 focus:outline-none"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sub Tab Content: Learning */}
                            {ultraSubTab === "learning" && (
                                <div className="space-y-6">
                                    <div className="grid lg:grid-cols-3 gap-6">
                                        {/* Simulation Form */}
                                        <div className="lg:col-span-1 rounded-xl border border-white/10 bg-[#0B0F19]/60 p-5 space-y-4 h-fit">
                                            <div className="flex items-center gap-2 text-[#D4AF37]">
                                                <Settings className="h-4 w-4" />
                                                <h4 className="text-sm font-bold">Pekiştirmeli Öğrenme Simülatörü</h4>
                                            </div>
                                            <p className="text-xs text-slate-400">
                                                Müvekkile gönderilen bir mesajın sonucunu (başarılı evrak yükleme / hızlı geri dönüş) 
                                                simüle ederek Akıl'ın başarı skorlarını pekiştirmeli öğrenme formülüyle güncellemesini izleyin.
                                            </p>
                                            <div className="space-y-3">
                                                <div className="space-y-1">
                                                    <label className="text-xs text-slate-400">WhatsApp Gönderim ID</label>
                                                    <input
                                                        type="number"
                                                        value={simGonderimId}
                                                        onChange={(e) => setSimGonderimId(e.target.value)}
                                                        placeholder="Örn: 104"
                                                        className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-xs text-slate-400">Geri Dönüş Başarılı mı?</label>
                                                    <select
                                                        value={simBasarili ? "true" : "false"}
                                                        onChange={(e) => setSimBasarili(e.target.value === "true")}
                                                        className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white"
                                                    >
                                                        <option value="true">Evet (Evrak Yüklendi / Cevap Verildi)</option>
                                                        <option value="false">Hayır (Cevapsız / Geri Dönüş Yok)</option>
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-xs text-slate-400">Geri Dönüş Süresi (Saat)</label>
                                                    <input
                                                        type="number"
                                                        value={simSüre}
                                                        onChange={(e) => setSimSüre(e.target.value)}
                                                        placeholder="Örn: 2"
                                                        className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-white"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleUltraAction({ 
                                                    action: "record_feedback", 
                                                    gonderim_id: simGonderimId, 
                                                    basarili: simBasarili,
                                                    geri_donus_suresi_saat: simSüre ? parseFloat(simSüre) : null
                                                })}
                                                disabled={ultraActionLoading || !simGonderimId}
                                                className="w-full flex items-center justify-center gap-2 text-xs px-4 py-2.5 rounded-lg bg-[#D4AF37] text-black font-semibold hover:bg-[#bfa02e] disabled:opacity-50 transition"
                                            >
                                                {ultraActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                                                Geri Bildirim Kaydet
                                            </button>
                                        </div>

                                        {/* Grid */}
                                        <div className="lg:col-span-2 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-bold text-white">Canlı Öğrenen Stil & İletişim Hafızası</h4>
                                                <span className="text-xs text-slate-500">Kayıt: {ultraData?.kapali_dongu?.length || 0}</span>
                                            </div>

                                            {(!ultraData?.kapali_dongu || ultraData.kapali_dongu.length === 0) ? (
                                                <div className="text-slate-500 text-sm py-10 text-center border border-white/5 bg-black/20 rounded-xl">
                                                    Henüz pekiştirmeli öğrenme kaydı bulunmuyor.
                                                </div>
                                            ) : (
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    {ultraData.kapali_dongu.map((item) => (
                                                        <div key={item.id} className="rounded-xl border border-white/10 bg-[#0B0F19]/40 p-4 space-y-3">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-xs font-bold text-[#D4AF37]">{item.kanal}</span>
                                                                <span className="text-xs font-mono text-slate-500">Saat: {item.gonderim_saati}:00</span>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="text-sm font-semibold text-white">
                                                                    Ton: <span className="text-blue-300">{item.ton}</span>
                                                                </div>
                                                                <div className="text-xs text-slate-400">
                                                                    Müvekkil Profili: <span className="text-slate-200 capitalize">{item.muvekkil_profili}</span>
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1.5 pt-2 border-t border-white/5">
                                                                <div className="flex justify-between text-xs">
                                                                    <span className="text-slate-400">Başarı Skoru:</span>
                                                                    <span className="font-bold text-emerald-400">{item.basari_skoru.toFixed(2)}</span>
                                                                </div>
                                                                <div className="w-full bg-black/50 h-2 rounded-full overflow-hidden">
                                                                    <div 
                                                                        className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                                                                        style={{ width: `${Math.min((item.basari_skoru / 5) * 100, 100)}%` }}
                                                                    />
                                                                </div>
                                                                {item.geri_donus_suresi_saat && (
                                                                    <div className="text-[10px] text-slate-500 text-right">
                                                                        Ort. Geri Dönüş: {item.geri_donus_suresi_saat.toFixed(1)} saat
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "ajanlar" && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
                                <Bot className="h-5 w-5" />
                                <h3 className="font-medium text-lg">Çoklu Ajan İş Akışları ve Onay Kuyruğu</h3>
                            </div>
                            {ajanGorevleri.length === 0 ? (
                                <div className="text-slate-500 text-sm py-6">Onay bekleyen aktif ajan görevi bulunmuyor.</div>
                            ) : (
                                <div className="space-y-3">
                                    {ajanGorevleri.map((g) => {
                                        const isExpanded = expandedGorev === g.id;
                                        return (
                                            <div key={g.id} className="rounded-xl border border-white/10 bg-[#0B0F19]/50 overflow-hidden">
                                                <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
                                                    <div>
                                                        <div className="text-xs text-slate-500">Dosya: {g.dosya_no}</div>
                                                        <h4 className="text-sm font-semibold text-white mt-0.5">{g.gorev_adi}</h4>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                                            g.durum === "ONAYLANDI"
                                                                ? "bg-emerald-500/10 text-emerald-400"
                                                                : g.durum === "REDDEDILDI"
                                                                  ? "bg-rose-500/10 text-rose-400"
                                                                  : "bg-amber-500/10 text-amber-400"
                                                        }`}>
                                                            {g.durum === "ONAY_BEKLIYOR" ? "Onay Bekliyor" : g.durum}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setExpandedGorev(isExpanded ? null : g.id)}
                                                            className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400"
                                                        >
                                                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                        </button>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div className="border-t border-white/10 p-4 space-y-4 bg-black/20">
                                                        {/* Ajan Tartışmaları */}
                                                        <div>
                                                            <div className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Ajan Tartışma Logu</div>
                                                            <div className="space-y-2 max-h-60 overflow-y-auto p-3 rounded-lg bg-black/40 border border-white/5">
                                                                {(g.ajan_tartismalari || []).map((t, idx) => (
                                                                    <div key={idx} className="text-xs flex gap-2">
                                                                        <span className={`font-bold shrink-0 ${
                                                                            t.ajan === "Araştırmacı"
                                                                                ? "text-blue-400"
                                                                                : t.ajan === "Hukukçu"
                                                                                  ? "text-purple-400"
                                                                                  : "text-amber-400"
                                                                        }`}>[{t.ajan}]:</span>
                                                                        <span className="text-slate-300">{t.mesaj}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Önerilen Aksiyon */}
                                                        <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
                                                            <div className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-1">Önerilen Aksiyon ({g.onerilen_aksiyon?.tip})</div>
                                                            <div className="text-sm font-bold text-white mb-2">{g.onerilen_aksiyon?.baslik}</div>
                                                            <div className="text-xs text-slate-300 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded border border-white/5">{g.onerilen_aksiyon?.metin}</div>
                                                        </div>

                                                        {/* Onay Butonları */}
                                                        {g.durum === "ONAY_BEKLIYOR" && (
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleAjanIslem(g.id, "reject")}
                                                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 transition"
                                                                >
                                                                    <XCircle className="h-3.5 w-3.5" /> Reddet
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleAjanIslem(g.id, "approve")}
                                                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition"
                                                                >
                                                                    <CheckCircle className="h-3.5 w-3.5" /> Onayla ve Uygula
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "saglik" && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                                <div className="flex items-center gap-2 text-rose-400">
                                    <ShieldAlert className="h-5 w-5" />
                                    <h3 className="font-medium text-lg">Proaktif Dosya Sağlığı ve Tutarsızlık Dedektörü</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={startHealthCheck}
                                    disabled={healthChecking}
                                    className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-50 transition"
                                >
                                    {healthChecking ? (
                                        <>
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Tarama Yapılıyor…
                                        </>
                                    ) : (
                                        <>
                                            <Activity className="h-3.5 w-3.5" /> Sağlık Taraması Başlat
                                        </>
                                    )}
                                </button>
                            </div>

                            {saglikUyarilari.length === 0 ? (
                                <div className="text-slate-500 text-sm py-6">Sistemde herhangi bir dosya sağlığı veya tutarsızlık uyarısı bulunmuyor. Tebrikler!</div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-3">
                                    {saglikUyarilari.map((u) => (
                                        <div
                                            key={u.id}
                                            className={`rounded-xl border p-4 flex gap-3 ${
                                                u.seviye === "danger"
                                                    ? "border-rose-500/30 bg-rose-500/5 text-rose-200"
                                                    : u.seviye === "warning"
                                                      ? "border-amber-500/30 bg-amber-500/5 text-amber-200"
                                                      : "border-sky-500/30 bg-sky-500/5 text-sky-200"
                                            }`}
                                        >
                                            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs font-mono bg-white/5 px-1.5 py-0.5 rounded text-slate-400">{u.dosya_no}</span>
                                                    <span className="text-xs font-mono bg-white/5 px-1.5 py-0.5 rounded text-slate-400">{u.plaka}</span>
                                                </div>
                                                <h4 className="text-sm font-bold mt-1.5">{u.baslik}</h4>
                                                <p className="text-xs opacity-80 mt-1 leading-relaxed">{u.aciklama}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "ocr" && (
                        <div className="rounded-2xl border border-white/10 p-6 space-y-4">
                            <div className="flex items-center gap-2 text-[#D4AF37]">
                                <Scan className="h-5 w-5" />
                                <h3 className="font-medium">Hasar faturası / PDF → Otomatik DK</h3>
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Hasar faturası veya ruhsat PDF/JPG yükleyin. Rayiç bulunamazsa hasar tutarından tahmini
                                hesap yapılır; eksik alanlar sohbette tamamlanabilir.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    ["hasar_faturasi", "Hasar faturası"],
                                    ["ruhsat", "Ruhsat"],
                                    ["otomatik", "Otomatik"],
                                ].map(([val, lbl]) => (
                                    <button
                                        key={val}
                                        type="button"
                                        onClick={() => setOcrBelgeTipi(val)}
                                        className={`rounded-lg px-3 py-1.5 text-xs font-medium border ${
                                            ocrBelgeTipi === val
                                                ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3E5AB]"
                                                : "border-white/10 text-slate-400 hover:border-white/20"
                                        }`}
                                    >
                                        {lbl}
                                    </button>
                                ))}
                            </div>
                            <label className="flex flex-col items-center justify-center border-2 border-dashed border-white/15 rounded-xl p-10 cursor-pointer hover:border-[#D4AF37]/40">
                                <Upload className="h-10 w-10 text-slate-500 mb-2" />
                                <span className="text-sm text-slate-400">
                                    {ocrLoading ? "OCR + hesap yapılıyor…" : "Dosya seç (PDF/JPG)"}
                                </span>
                                <input type="file" className="hidden" accept="image/*,.pdf" onChange={ocrUpload} disabled={ocrLoading} />
                            </label>
                            {ocrSonuc?.success && ocrSonuc?.hesap?.gercek_deger_kaybi ? (
                                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm space-y-2">
                                    <div className="text-emerald-300 font-semibold">
                                        Sonuç: ₺{Number(ocrSonuc.hesap.gercek_deger_kaybi).toLocaleString("tr-TR")}
                                        {ocrSonuc.rayic_tahmini ? (
                                            <span className="text-amber-300/90 font-normal text-xs ml-2">
                                                (rayiç tahmini — doğrulayın)
                                            </span>
                                        ) : null}
                                    </div>
                                    {ocrSonuc.hesap?.tespit_ozeti ? (
                                        <p className="text-xs text-slate-400">{ocrSonuc.hesap.tespit_ozeti}</p>
                                    ) : null}
                                    <Link to="/ai/sohbet" className="inline-block text-[#D4AF37] underline text-xs">
                                        Sohbette devam et
                                    </Link>
                                </div>
                            ) : null}
                            {ocrSonuc && !ocrSonuc.success ? (
                                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm space-y-2">
                                    <p className="text-rose-200 font-medium">{ocrSonuc.error}</p>
                                    {ocrSonuc.hint ? <p className="text-xs text-slate-400">{ocrSonuc.hint}</p> : null}
                                    {ocrSonuc.eksik_alanlar?.length ? (
                                        <p className="text-xs text-amber-300/90">
                                            Eksik: {ocrSonuc.eksik_alanlar.join(", ")}
                                        </p>
                                    ) : null}
                                    {ocrSonuc.ocr?.ham_metin_ozet ? (
                                        <pre className="text-[10px] text-slate-500 max-h-24 overflow-auto whitespace-pre-wrap bg-black/20 p-2 rounded">
                                            {ocrSonuc.ocr.ham_metin_ozet}
                                        </pre>
                                    ) : null}
                                </div>
                            ) : null}
                            {komutSonuc?.gercek_deger_kaybi && !ocrSonuc ? (
                                <div className="text-emerald-300 text-sm">
                                    Sonuç: ₺{Number(komutSonuc.gercek_deger_kaybi).toLocaleString("tr-TR")}
                                    <Link to="/ai/sohbet" className="ml-3 underline text-[#D4AF37]">
                                        Sohbette devam et
                                    </Link>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {tab === "komut" && (
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <input
                                    value={komut}
                                    onChange={(e) => setKomut(e.target.value)}
                                    placeholder="/dk hesapla veya /senaryo …"
                                    className="flex-1 rounded-xl bg-[#0B0F19] border border-white/10 px-4 py-3 text-sm"
                                />
                                <button type="button" onClick={runKomut} className="px-4 rounded-xl bg-[#D4AF37] text-black">
                                    <Terminal className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="grid md:grid-cols-2 gap-2">
                                {(data?.komut_paleti || []).map((c) => (
                                    <button
                                        key={c.cmd}
                                        type="button"
                                        onClick={() => setKomut(c.cmd + " " + (c.ornek || ""))}
                                        className="text-left text-xs p-3 rounded-lg border border-white/10 hover:border-[#D4AF37]/30"
                                    >
                                        <code className="text-[#D4AF37]">{c.cmd}</code>
                                        <div className="text-slate-500 mt-1">{c.ornek}</div>
                                    </button>
                                ))}
                            </div>
                            {komutSonuc && (
                                <div className="rounded-xl border border-white/10 p-4 text-sm">
                                    <AiMarkdown text={JSON.stringify(komutSonuc, null, 2).slice(0, 3000)} />
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "tahkim" && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-purple-300">
                                <Scale className="h-5 w-5" />
                                <span>Tahkim + Değer Kaybı birleşik paket</span>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    value={paketDosya}
                                    onChange={(e) => setPaketDosya(e.target.value)}
                                    placeholder="MBS dosya no"
                                    className="flex-1 rounded-xl bg-[#0B0F19] border border-white/10 px-4 py-3 text-sm"
                                />
                                <button type="button" onClick={tahkimPaket} className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm">
                                    Paket oluştur
                                </button>
                            </div>
                            {paketSonuc?.metin && <AiMarkdown text={paketSonuc.metin} />}
                        </div>
                    )}

                    {tab === "kritik" && (
                        <div className="space-y-2">
                            {(data?.kritik_dosyalar || []).map((d) => (
                                <div
                                    key={d.sigorta_dosyasi_id}
                                    className="flex items-center justify-between rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3"
                                >
                                    <div>
                                        <div className="text-sm text-white">{d.dosya_no} · {d.plaka}</div>
                                        <div className="text-xs text-slate-500">{d.oneriler?.join(" · ")}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-rose-400" />
                                        <span className="font-bold text-rose-300">{d.skor}/100</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </AiPageShell>
    );
}
